import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Button from "src/components/Button";
import TextInput from "src/components/TextInput";
import { ActiveCardioExerciseInterval, useEndActiveWorkoutIntervalMutation } from "src/graphql";
import { singleUnitToString, rangeUnitToString } from "../utils/range-unit-to-string";
import { CheckCircle as CheckIcon } from 'react-huge-icons/solid'

const CardioExercise = (props: {
    interval: ActiveCardioExerciseInterval, idx: number, workoutExerciseId: string
}) => {

    const { interval, idx, workoutExerciseId } = props;

    const current = Object.keys(interval).filter((d) => d !== '__typename').reduce<any>((acc, key) => {
        acc[key] = (interval[key as keyof typeof interval] as any)?.min || (interval[key as keyof typeof interval] as any)?.val
        return acc;
    }, {})

    const { t } = useTranslation();

    const { handleSubmit, register, formState: { isSubmitting } } = useForm({
        defaultValues: {
            distance: interval.distance?.done || interval.distance?.val,
            duration: interval.duration?.done || interval.duration?.val,
            tempo: interval.tempo?.done || interval.tempo?.val,
            intensity: interval.intensity?.done || interval.intensity?.val,
            angle: interval.angle?.done || interval.angle?.val,
            resistance: interval.resistance?.done || interval.resistance?.val,
        }
    });

    const onSubmit = async (e: any) => {
        await endActiveWorkoutInterval({
            variables: {
                input: {
                    exerciseId: workoutExerciseId,
                    intervalId: interval.id,
                    ...e
                }
            },
            update(cache, result, options) {


                cache.modify({
                    id: cache.identify({ id: options.variables?.input.intervalId, __typename: 'ActiveCardioExerciseInterval' }),
                    fields: {
                        end_at() {
                            return new Date().toISOString()
                        },
                        distance(data) {
                            return {
                                ...data,
                                done: options.variables?.input.distance
                            };
                        },
                        duration(data) {
                            return {
                                ...data,
                                done: options.variables?.input.duration
                            };
                        },
                        tempo(data) {
                            return {
                                ...data,
                                done: options.variables?.input.tempo
                            };
                        },
                        intensity(data) {
                            return {
                                ...data,
                                done: options.variables?.input.intensity
                            };
                        },
                        angle(data) {
                            return {
                                ...data,
                                done: options.variables?.input.angle
                            };
                        },
                        resistance(data) {
                            return {
                                ...data,
                                done: options.variables?.input.resistance
                            };
                        },
                        rest(data) {
                            return {
                                ...data,
                                done: options.variables?.input.rest
                            };
                        }
                    }
                })
            }
        })
    }

    const [endActiveWorkoutInterval, { loading: isEndActiveWorkoutIntervalLoading }] = useEndActiveWorkoutIntervalMutation();

    return (
        <div key={idx} className="gap-x-3">
            <div className="flex gap-2 text-center flex-col">
                {Boolean(current.distance) && <div className='gap-2 items-center flex'>
                    <div className="basis-1/2 flex flex-col">
                        <p className='light'>
                            {t("Distance")}
                        </p>
                        <div>
                            {singleUnitToString(interval.distance!, t)}
                        </div>
                    </div>
                    <span>עשיתי:</span>
                    <TextInput inputProps={{ ...register("distance") }} type="number" maxLength={3} value={String(interval?.distance?.done || interval?.distance?.val)} />
                </div>}

                {Boolean(current.duration) && <div className='gap-2 items-center flex'>
                    <div className="basis-1/2 flex flex-col">
                        <p className='light'>
                            {t("Duration")}
                        </p>
                        <div>
                            {singleUnitToString(interval.duration!, t)}
                        </div>
                    </div>
                    <span>עשיתי:</span>
                    <TextInput inputProps={{ ...register("duration") }} type="number" maxLength={3} value={String(interval?.duration?.done || interval?.duration?.val)} />
                </div>}

                {Boolean(current.tempo) && <div className='gap-2 items-center flex'>
                    <div className="basis-1/2 flex flex-col">
                        <p className='light'>
                            {t("Tempo")}
                        </p>
                        <div>
                            {singleUnitToString(interval.tempo!, t)}
                        </div>
                    </div>
                    <span>עשיתי:</span>
                    <TextInput inputProps={{ ...register("tempo") }} type="number" maxLength={3} value={String(interval?.tempo?.done || interval?.tempo?.val)} />
                </div>}

                {Boolean(current.intensity) && <div className='gap-2 items-center flex'>
                    <div className="basis-1/2 flex flex-col">
                        <p className='light'>
                            {t("Intensity")}
                        </p>
                        <div>
                            {rangeUnitToString(interval.intensity!, t)}
                        </div>
                    </div>
                </div>}

                {Boolean(current.target_heart_rate) && <div className='gap-2 items-center flex'>
                    <div className="basis-1/2 flex flex-col">
                        <p className='light'>
                            {t("Target Heart Rate")}
                        </p>
                        <div>
                            {rangeUnitToString(interval.target_heart_rate!, t)}
                        </div>
                    </div>
                </div>}

                {Boolean(current.angle) && <div className='gap-2 items-center flex'>
                    <div className="basis-1/2 flex flex-col">
                        <p className='light'>
                            {t("Angle")}
                        </p>
                        <div>
                            {singleUnitToString(interval.angle!, t)}
                        </div>
                    </div>
                </div>}

                {Boolean(current.resistance) && <div className='gap-2 items-center flex'>
                    <div className="basis-1/2 flex flex-col">
                        <p className='light'>
                            {t("Resistance")}
                        </p>
                        <div>
                            {singleUnitToString(interval.resistance!, t)}
                        </div>
                    </div>
                </div>}

                <Button loading={isEndActiveWorkoutIntervalLoading} link onClick={async () => {
                    await handleSubmit(onSubmit)()
                }}>
                    <CheckIcon className={Boolean(interval.end_at) ? 'text-green-500' : 'text-red-500'} fontSize={50} />
                </Button>

            </div>
        </div>

    )
}
export default CardioExercise